import React, { useCallback, useMemo, useState } from "react";
import {
  Card,
  Divider,
  Modal,
  Stack,
  StackProps,
  Typography,
} from "@mui/material";
import { StyledIcon } from "../../../components/StyledIcon/StyledIcon";
import { Textarea } from "./Textarea";
import { useAuth } from "../../../contexts/authentication/AuthenticationContext";
import { initialValues, State } from "../constants/schema";
import { useIocContext } from "../../../contexts/ioc/IocContext";
import { IStorageService } from "../../../modules/storage/models/IStorageService";
import { Types } from "../../../ioc/types";
import {
  CreateSchedule,
  ICustomerCompany,
  IProduct,
  StorageEndpoint,
} from "../../../modules/storage/models/storage-dto";
import useDialogAlert from "../../../hooks/DialogAlert";
import AppError from "../../../utils/appError";
import { convertToDefaultDate } from "../../../utils/dateFormatter";
import { useNavigate } from "react-router-dom";
import { routesConfig } from "../../../config/routes";
import { ComponentFactory } from "../constants/component-factory";
import CustomButton from "../../../components/CustomButton/CustomButton";
import { SubsidiaryDTO } from "../../../modules/distributors/models/DistributorListDTO";

interface Props {
  id: string;
  open: boolean;
  onClose: VoidFunction;
  data: State;
  payload: typeof initialValues;
  endpoint: StorageEndpoint;
  lists: {
    companies: ICustomerCompany[];
    products: IProduct[];
    subsidiaries: SubsidiaryDTO[];
  };
}

interface DataFieldProps extends StackProps {
  title: string;
  subtitle: string;
  axis?: StackProps["flexDirection"];
}

const DataField: React.FC<DataFieldProps> = ({
  title,
  subtitle,
  axis = "column",
  sx,
  ...props
}) => (
  <Stack sx={{ flexDirection: axis, alignItems: "center", ...sx }} {...props}>
    <Typography
      color="shadesOfDark.dark"
      sx={{ fontWeight: 700, fontSize: "1.6rem" }}
    >
      {title}
    </Typography>
    <Typography color="shadesOfDark.dark">{subtitle}</Typography>
  </Stack>
);

const RequestModal = React.memo<Props>(
  ({ open, onClose, data, payload, id, endpoint, lists }) => {
    const [loading, setLoading] = useState<boolean>(false);

    const authContext = useAuth();
    const navigate = useNavigate();
    const { snackbar } = useDialogAlert();
    const { serviceContainer } = useIocContext();

    const { getRowsItems, getActionButtons } = ComponentFactory;

    const storageService = serviceContainer.get<IStorageService>(
      Types.Storage.IStorageService
    );

    const { hash } = window.location;
    const slug = hash.split("#")[1];
    const isEditing = Boolean(id);

    const isDischarge = useMemo(
      () => slug.includes(routesConfig.STORAGE().CREATE_SCHEDULING),
      []
    );

    const parsedData: CreateSchedule = useMemo(
      () => ({
        customerCompanyId: payload.client,
        subsidiaryId: payload.codeSubsidiary,
        volumeRequested: parseInt(payload.quantity),
        mod: payload.modality,
        scheduleDate: convertToDefaultDate(payload.date),
        scheduleTime: payload.time,
        productId: payload.product,
        note: data.note ?? "",
        paymentCondition: "0000",
        invoice: payload.invoice ?? "",
      }),
      [payload]
    );

    const ROWS_ITEMS = getRowsItems(
      data,
      String(authContext?.name?.split(" ")[0] ?? ""),
      lists
    );

    const ACTION_BUTTONS = getActionButtons(loading, [
      onClose,
      () => {
        handleCreateSchedule();
      },
    ]);

    const handleCreateSchedule = useCallback(async () => {
      try {
        setLoading(true);

        for (const key in parsedData) {
          if (parsedData[key] === "" || parsedData[key] === null) {
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete parsedData[key];
          }
        }

        if (isEditing) {
          const { volumeRequested, note } = parsedData;

          if (isDischarge) {
            await storageService.updateUnloadCustomer(id, {
              volumeRequested: Number(volumeRequested),
              note,
            });
          } else {
            await storageService.updateRetrievalCustomer(id, {
              volumeRequested: Number(volumeRequested),
              note,
            });
          }
        } else {
          await storageService.createSchedule(endpoint, parsedData);
        }

        snackbar({
          message: "Programação agendada com sucesso!",
          variant: "success",
        });

        navigate(
          routesConfig.STORAGE()[isDischarge ? "RIVER_DISCHARGES" : "PIPELINES"]
        );
      } catch (error) {
        snackbar({
          message: (error as AppError).message,
          variant: "error",
        });
      } finally {
        setLoading(false);
      }
    }, []);

    return (
      <Modal open={open} onClose={onClose} sx={styles.root}>
        <Card sx={styles.card}>
          <Stack>
            <Typography color="shadesOfDark.dark" sx={styles.header}>
              Detalhes do pedido de {isDischarge ? "descarga" : "entrega"}
            </Typography>
          </Stack>

          <Stack sx={styles.first_container}>
            <Stack flexDirection="row" alignItems="center" gap="2rem">
              <StyledIcon iconType="list" sx={styles.icon} />
              <DataField
                title={ROWS_ITEMS[0].label}
                subtitle={ROWS_ITEMS[0].data as string}
              />
            </Stack>

            <Stack sx={{ flexDirection: "row", gap: "5rem" }}>
              {ROWS_ITEMS.slice(1, 3).map(({ label, data }) => (
                <DataField
                  key={label}
                  title={label}
                  subtitle={data as string}
                />
              ))}
            </Stack>
          </Stack>

          <Stack sx={styles.second_container}>
            {ROWS_ITEMS.slice(3, 5).map(({ label, data }) => (
              <DataField
                key={label}
                title={label}
                subtitle={data as string}
                axis="row"
                sx={{ gap: ".5rem" }}
              />
            ))}
          </Stack>

          <Stack sx={styles.third_container}>
            {ROWS_ITEMS.slice(5).map((item) => {
              return item.data ? (
                <DataField
                  key={item.label}
                  title={item.label}
                  subtitle={item.data}
                />
              ) : null;
            })}
          </Stack>

          {data.note && (
            <Stack>
              <Typography
                fontFamily={"Open Sans"}
                fontWeight={700}
                color={"shadesOfDark.dark"}
                mb={0.5}
                mt="1rem"
              >
                Observação
              </Typography>

              <Textarea value={data.note} disabled />
            </Stack>
          )}

          <Divider sx={styles.divider} />

          <Stack sx={styles.buttons_content}>
            {ACTION_BUTTONS.map(({ title, variant, onClick }) => (
              <CustomButton
                key={title}
                title={title}
                variant={variant}
                onClick={onClick}
              />
            ))}
          </Stack>
        </Card>
      </Modal>
    );
  }
);

const styles = {
  root: { display: "flex", justifyContent: "center", alignItems: "center" },
  card: {
    padding: "2rem",
    border: "none",
    boxShadow: "none",
    display: "flex",
    flexDirection: "column",
    rowGap: "1rem",
    width: "80rem",
  },
  header: { fontWeight: 700, fontSize: "2rem" },
  first_container: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingRight: "2rem",
  },
  icon: { height: "7rem", width: "7rem" },
  second_container: {
    flexDirection: "row",
    alignItems: "center",
    gap: "2rem",
    mt: "5rem",
  },
  third_container: {
    border: "1px solid #DFE0EB",
    p: "1.5rem",
    flexDirection: "row",
    justifyContent: "space-between",
    borderRadius: "4px",
  },
  divider: { m: "2rem -2rem", borderColor: "#DFE0EB" },
  buttons_content: {
    justifyContent: "flex-end",
    flexDirection: "row",
    gap: "1rem",
  },
};

export { RequestModal };
